export * from "./types";
export * from "./constants";
export * from "./predictor";
export * from "./state-updater";
export * from "./group-manager";
export * from "./session-builder";

import { prisma } from "@/lib/db";
import { updateGroupStateAfterAnswer } from "./group-manager";
import {
  checkAnswer,
  updateStateAfterAnswer,
} from "./state-updater";
import type { AnswerInput, Grade, PresentedType, ProblemStatus } from "./types";

export interface RecordAnswerResult {
  wasCorrect: boolean;
  grade: Grade;
  correctAnswer: string;
  answerDescription: string;
  newState: {
    status: ProblemStatus;
    memoryStrength: number;
    difficulty: number;
    dueAt: Date;
    consecutiveCorrect: number;
    consecutiveWrong: number;
  };
}

/**
 * Record an answer and update all relevant state
 */
export async function recordAnswer(
  input: AnswerInput
): Promise<RecordAnswerResult> {
  const now = new Date();

  // Get problem and current state
  const problem = await prisma.problem.findUnique({
    where: { id: input.problemId },
  });

  if (!problem) {
    throw new Error(`Problem not found: ${input.problemId}`);
  }

  const currentState = await prisma.userProblemState.findUnique({
    where: {
      userId_problemId: {
        userId: input.userId,
        problemId: input.problemId,
      },
    },
  });

  // Determine correctness
  const wasCorrect = checkAnswer(input.userInput, problem.answer);

  // Calculate new state
  const stateInput = currentState
    ? {
        status: currentState.status as ProblemStatus,
        introducedAt: currentState.introducedAt,
        memoryStrength: currentState.memoryStrength,
        difficulty: currentState.difficulty,
        successCount: currentState.successCount,
        failureCount: currentState.failureCount,
        consecutiveCorrect: currentState.consecutiveCorrect,
        consecutiveWrong: currentState.consecutiveWrong,
        recentAvgResponseMs: currentState.recentAvgResponseMs,
        seenCountToday: currentState.seenCountToday,
      }
    : {
        status: "new" as ProblemStatus,
        introducedAt: null,
        memoryStrength: 0.15,
        difficulty: 0.5,
        successCount: 0,
        failureCount: 0,
        consecutiveCorrect: 0,
        consecutiveWrong: 0,
        recentAvgResponseMs: null,
        seenCountToday: 0,
      };

  const newState = updateStateAfterAnswer(
    stateInput,
    wasCorrect,
    input.responseMs,
    now
  );

  // Update problem state
  await prisma.userProblemState.upsert({
    where: {
      userId_problemId: {
        userId: input.userId,
        problemId: input.problemId,
      },
    },
    update: {
      status: newState.status,
      lastSeenAt: newState.lastSeenAt,
      lastAnsweredAt: newState.lastAnsweredAt,
      dueAt: newState.dueAt,
      memoryStrength: newState.memoryStrength,
      difficulty: newState.difficulty,
      successCount: newState.successCount,
      failureCount: newState.failureCount,
      consecutiveCorrect: newState.consecutiveCorrect,
      consecutiveWrong: newState.consecutiveWrong,
      recentAvgResponseMs: newState.recentAvgResponseMs,
      lastResponseMs: newState.lastResponseMs,
      lastResult: newState.lastResult,
      lastGrade: newState.lastGrade,
      seenCountToday: newState.seenCountToday,
    },
    create: {
      userId: input.userId,
      problemId: input.problemId,
      status: newState.status,
      introducedAt: newState.introducedAt,
      lastSeenAt: newState.lastSeenAt,
      lastAnsweredAt: newState.lastAnsweredAt,
      dueAt: newState.dueAt,
      memoryStrength: newState.memoryStrength,
      difficulty: newState.difficulty,
      confidence: 0.2,
      successCount: newState.successCount,
      failureCount: newState.failureCount,
      consecutiveCorrect: newState.consecutiveCorrect,
      consecutiveWrong: newState.consecutiveWrong,
      recentAvgResponseMs: newState.recentAvgResponseMs,
      lastResponseMs: newState.lastResponseMs,
      lastResult: newState.lastResult,
      lastGrade: newState.lastGrade,
      seenCountToday: newState.seenCountToday,
    },
  });

  // Create review log
  await prisma.reviewLog.create({
    data: {
      userId: input.userId,
      problemId: input.problemId,
      sessionId: input.sessionId,
      presentedType: input.presentedType,
      userInput: input.userInput,
      wasCorrect,
      grade: newState.lastGrade,
      responseMs: input.responseMs,
    },
  });

  // Update session stats
  await prisma.studySession.update({
    where: { id: input.sessionId },
    data: {
      completedCount: { increment: 1 },
      correctCount: wasCorrect ? { increment: 1 } : undefined,
    },
  });

  // Update group state
  await updateGroupStateAfterAnswer(input.userId, input.problemId, now);

  return {
    wasCorrect,
    grade: newState.lastGrade,
    correctAnswer: problem.answer,
    answerDescription: problem.answerDescription,
    newState: {
      status: newState.status,
      memoryStrength: newState.memoryStrength,
      difficulty: newState.difficulty,
      dueAt: newState.dueAt,
      consecutiveCorrect: newState.consecutiveCorrect,
      consecutiveWrong: newState.consecutiveWrong,
    },
  };
}

/**
 * Get user's study statistics
 */
export async function getUserStats(userId: string) {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const [
    totalProblems,
    introducedProblems,
    masteredProblems,
    dueProblems,
    todayLogs,
    allLogs,
  ] = await Promise.all([
    prisma.problem.count(),
    prisma.userProblemState.count({
      where: { userId, status: { not: "new" } },
    }),
    prisma.userProblemState.count({
      where: { userId, status: "mastered" },
    }),
    prisma.userProblemState.count({
      where: { userId, dueAt: { lte: now }, status: { notIn: ["new", "mastered"] } },
    }),
    prisma.reviewLog.findMany({
      where: { userId, timestamp: { gte: startOfToday } },
    }),
    prisma.reviewLog.findMany({
      where: { userId },
      orderBy: { timestamp: "desc" },
      take: 100,
    }),
  ]);

  const todayCorrect = todayLogs.filter((l) => l.wasCorrect).length;
  const todayTotal = todayLogs.length;

  const recentCorrect = allLogs.filter((l) => l.wasCorrect).length;
  const recentTotal = allLogs.length;

  return {
    totalProblems,
    introducedProblems,
    masteredProblems,
    dueProblems,
    todayStats: {
      answered: todayTotal,
      correct: todayCorrect,
      accuracy: todayTotal > 0 ? todayCorrect / todayTotal : 0,
    },
    recentStats: {
      answered: recentTotal,
      correct: recentCorrect,
      accuracy: recentTotal > 0 ? recentCorrect / recentTotal : 0,
    },
  };
}
